import { useState, forwardRef } from "react"
import { Eye, EyeOff } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input, type InputProps } from "../Input/Input"

export interface PasswordInputProps extends Omit<InputProps, "type"> {
  visible?: boolean
  defaultVisible?: boolean
  onVisibilityChange?: (visible: boolean) => void
  toggleAriaLabel?: string
  wrapperClassName?: string
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      className,
      wrapperClassName,
      visible,
      defaultVisible = false,
      onVisibilityChange,
      toggleAriaLabel = "Toggle password visibility",
      ...props
    },
    ref,
  ) => {
    const [internalVisible, setInternalVisible] = useState(defaultVisible)
    const isControlled = visible !== undefined
    const isVisible = isControlled ? visible : internalVisible

    const toggle = () => {
      const next = !isVisible
      if (!isControlled) setInternalVisible(next)
      onVisibilityChange?.(next)
    }

    return (
      <div className={cn("relative", wrapperClassName)}>
        <Input
          ref={ref}
          type={isVisible ? "text" : "password"}
          className={cn("pr-9", className)}
          {...props}
        />
        <button
          type="button"
          onClick={toggle}
          aria-label={toggleAriaLabel}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          {isVisible ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    )
  },
)
PasswordInput.displayName = "PasswordInput"
